import { useEffect } from 'react'
import { useCameraStore } from '@modugen/ui-toolkit'
import {
  useCommentFilterStore,
  useControlStoreApi,
  useEditorViewStore,
  useFloorplanStore,
  useModelStore,
  usePlanImageCardStore,
  usePlanTableStore,
} from '@editorStores'
import { getPlanViewStorey, resolvePlanViewStoreys, toPlanViewPlanType } from '../utils'
import { ApplyPlanViewPreset } from './usePlanViewPreset'

/**
 * Scene session of the plan view. On enter it applies the plan preset for the current plan and
 * storey (so the scene shows the plan right away) and shows the elements hidden by hand on other
 * pages (the export draws them) and hides the storey background images and the area / point / line
 * loads (the export draws none of them), and on leave it restores every scene setting the plan view
 * drove — element/label/load visibility (incl. per-storey type toggles and hand-hidden elements),
 * coloring mode, wall footprints, plan area, plan filter, storey and camera — so the other pages
 * continue where the user left them. A mount effect by design:
 * entering and leaving the route are the outside-world events being synced (same pattern as the
 * slab-beam visibility in Results2).
 */
const usePlanViewSceneSession = (applyPreset: ApplyPlanViewPreset): void => {
  const controlStoreApi = useControlStoreApi()
  const setPlanViewPlanChoice = useEditorViewStore(state => state.setPlanViewPlanChoice)

  useEffect(() => {
    const modelState = useModelStore.getState()
    const controlState = controlStoreApi.getState()
    const snapshot = {
      visibilityByType: { ...modelState.visibilityByType },
      visibilityByTypeAndStorey: Object.fromEntries(
        Object.entries(modelState.visibilityByTypeAndStorey).map(([type, storeys]) => [
          type,
          [...storeys],
        ]),
      ) as Record<ElementTypes, string[]>,
      hiddenElementIds: [...controlState.hiddenElementIds],
      labelVisibilityByType: { ...modelState.labelVisibilityModel.visibilityByType },
      showRealWallThickness: modelState.showRealWallThickness,
      showPlanArea: modelState.showPlanArea,
      showComments: modelState.showComments,
      visibleStoreys: [...modelState.visibleStoreys],
      showLabels: controlState.showLabels,
      showAreaLoads: controlState.structuralPlanningElementVisibility.areaLoads,
      showPointLoads: controlState.structuralPlanningElementVisibility.pointLoads,
      showLineLoads: controlState.structuralPlanningElementVisibility.lineLoads,
      showLabelCrossSections: controlState.showLabelCrossSections,
      coloringMode: controlState.coloringMode,
      floorplanStorey: controlState.floorplanState.storey,
      selectedPlan: useCommentFilterStore.getState().selectedPlan,
      tablesVisible: usePlanTableStore.getState().isVisible,
      imagesVisible: usePlanImageCardStore.getState().isVisible,
      storeyImageVisibilityIn2DByStorey: {
        ...useFloorplanStore.getState().storeyImageVisibilityIn2DByStorey,
      },
      isOrthographic: useCameraStore.getState().isOrthographic,
    }

    const planType = toPlanViewPlanType(snapshot.selectedPlan)
    const storeys = [...modelState.availableStoreys]
    // Enters on a single storey (the other pages' visible storeys are no plan selection).
    const planStorey = getPlanViewStorey(snapshot.floorplanStorey, modelState.visibleStoreys)
    const planStoreys = resolvePlanViewStoreys(planStorey ? [planStorey] : [], storeys, planType)
    if (planStoreys.length) applyPreset(planType, planStoreys)
    // Entering always previews a plan, so the Plantyp (store) matches what the scene shows.
    setPlanViewPlanChoice(planType)
    controlState.setAllElementsVisible()
    controlState.changeStructuralPlanningElementVisibility('areaLoads', false)
    controlState.changeStructuralPlanningElementVisibility('pointLoads', false)
    controlState.changeStructuralPlanningElementVisibility('lineLoads', false)
    useFloorplanStore
      .getState()
      .setStoreyImageVisibilityIn2DByStorey(Object.fromEntries(storeys.map(s => [s, false])))

    return () => {
      const model = useModelStore.getState()
      const control = controlStoreApi.getState()
      ;(Object.keys(snapshot.visibilityByType) as ElementTypes[]).forEach(type =>
        model.setTypeVisibility(type, snapshot.visibilityByType[type]),
      )
      ;(Object.keys(snapshot.visibilityByTypeAndStorey) as ElementTypes[]).forEach(type =>
        model.setTypeVisibilityByStorey(type, snapshot.visibilityByTypeAndStorey[type]),
      )
      model.setLabelVisibilityForTypes(
        (Object.keys(snapshot.labelVisibilityByType) as ElementTypes[]).filter(
          type => snapshot.labelVisibilityByType[type],
        ),
      )
      model.setShowRealWallThickness(snapshot.showRealWallThickness)
      model.setShowPlanArea(snapshot.showPlanArea)
      model.setShowComments(snapshot.showComments)
      model.setVisibleStoreys(snapshot.visibleStoreys)
      control.setAllElementsVisible()
      control.addHiddenElementIds(snapshot.hiddenElementIds)
      control.setShowLabels(snapshot.showLabels)
      control.changeStructuralPlanningElementVisibility('areaLoads', snapshot.showAreaLoads)
      control.changeStructuralPlanningElementVisibility('pointLoads', snapshot.showPointLoads)
      control.changeStructuralPlanningElementVisibility('lineLoads', snapshot.showLineLoads)
      control.setShowLabelCrossSections(snapshot.showLabelCrossSections)
      control.setColoringMode(snapshot.coloringMode)
      control.setFloorplanStorey(snapshot.floorplanStorey)
      useCommentFilterStore.getState().setSelectedPlan(snapshot.selectedPlan)
      usePlanTableStore.getState().setIsVisible(snapshot.tablesVisible)
      usePlanImageCardStore.getState().setIsVisible(snapshot.imagesVisible)
      useFloorplanStore
        .getState()
        .setStoreyImageVisibilityIn2DByStorey(snapshot.storeyImageVisibilityIn2DByStorey)
      useCameraStore.getState().setIsOrthographic(snapshot.isOrthographic)
    }
  }, [applyPreset, controlStoreApi, setPlanViewPlanChoice])
}

export default usePlanViewSceneSession
